import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, OnInit } from '@angular/core';
import { palatalFeedbackToggleClicked, restoMultiBiteClicked, restoMultiBiteToggleClicked } from '@core/services/logger/bi-actions';
import { BiLoggerService } from '@core/services/logger/bi-logger.service';
import { TlkBannerComponent } from '@itero/ui-toolkit-angular/banner';
import { TlkCardComponent } from '@itero/ui-toolkit-angular/card';
import { TlkCheckboxComponent } from '@itero/ui-toolkit-angular/checkbox';
import { TlkSlideToggleComponent } from '@itero/ui-toolkit-angular/slide-toggle';
import { TypeEnum } from '@modules/order/models/type.enum';
import { RxIntuitiveScanOptionsFacade } from '@modules/rx-intuitive-scan-options/rx-intutive-scan-options.facade';
import { Bites } from '@modules/teeth-diagram/models/bites.enum';
import { TranslateModule } from '@ngx-translate/core';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { ProcedureTypeService } from '@shared/components/rx-procedure-type-control/procedure-type.service';
import { additionalBites } from '@shared/data/bites';
import { debounceDelay } from '@shared/models/consts';
import { StaticFilesEndpointPipe } from '@shared/pipes/static-files-endpoint.pipe';
import { combineLatest, debounceTime, distinctUntilChanged, Observable, shareReplay, take, takeUntil } from 'rxjs';

@Component({
	selector: 'rx-intuitive-scan-options',
	templateUrl: './rx-intuitive-scan-options.component.html',
	styleUrls: ['./rx-intuitive-scan-options.component.scss'],
	providers: [RxIntuitiveScanOptionsFacade, ProcedureTypeService],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		CommonModule,
		TranslateModule,
		TlkBannerComponent,
		TlkCardComponent,
		TlkSlideToggleComponent,
		TlkCheckboxComponent,
		StaticFilesEndpointPipe
	]
})
export class RxIntuitiveScanOptionsComponent extends BaseDestroyableComponent implements OnInit {
	isReadOnly$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isReadOnly$;
	isDisabled$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isDisabled$;

	isNiriCaptureVisible$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isNiriCaptureVisible$;
	isNiriCaptureChecked$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isNiriCaptureChecked$;
	isNiriCaptureDisabled$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isNiriCaptureDisabled$;

	isPalatalFeedbackVisible$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isPalatalFeedbackVisible$;
	isPalatalFeedbackChecked$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isPalatalFeedbackChecked$;

	isSleeveConfirmationVisible$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isSleeveConfirmationVisible$;
	isSleeveChecked$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isSleeveChecked$;
	isSleeveConfirmationReadOnly$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isSleeveConfirmationReadOnly$;

	isPreTreatmentVisible$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isPreTreatmentVisible$;
	isPreTreatmentChecked$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isPreTreatmentChecked$;
	isPreTreatmentDisabled$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isPreTreatmentReadOnly$;

	isDentureCopyScanVisible$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isDentureCopyScanVisible$;
	isDentureCopyScan$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isDentureCopyScan$;
	isRxTakenForScan$ = this.rxIntuitiveScanOptionsFacade.isRxTakenForScan$;

	isEmergenceProfileChecked$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isEmergenceProfileChecked$;
	isEmergenceProfileDisabled$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isEmergenceProfileDisabled$;
	isEmergenceProfileVisible$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isEmergenceProfileVisible$;

	isMultiBiteVisible$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isMultiBiteVisible$;
	isMultiBiteSelected$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isMultiBiteSelected$.pipe(
		shareReplay({ bufferSize: 1, refCount: true })
	);

	isRestoMultiBiteVisible$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isRestoMultiBiteVisible$;
	isRestoMultiBiteSelected$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isRestoMultiBiteSelected$.pipe(
		shareReplay({ bufferSize: 1, refCount: true })
	);

	isAdditionalBitesVisible$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isAdditionalBitesVisible$;

	additionalBites = additionalBites;

	isOrthoModelIcastVisible$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isOrthoModelIcastVisible$;
	isOrthoModelIcastChecked$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isOrthoModelIcastChecked$;
	isOrthoModelIcastDisabled$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isOrthoModelIcastDisabled$;

	isSectionVisible$: Observable<boolean> = this.rxIntuitiveScanOptionsFacade.isSectionVisible$;
	isMultiBiteInfoBannerVisible$ = this.rxIntuitiveScanOptionsFacade.shouldShowMultiBiteInformationMessage$;

	palatalFeedbackClicked$: EventEmitter<boolean> = new EventEmitter<boolean>();

	constructor(
		private readonly rxIntuitiveScanOptionsFacade: RxIntuitiveScanOptionsFacade,
		private readonly biLogger: BiLoggerService,
		private readonly procedureTypeService: ProcedureTypeService
	) {
		super();
	}

	ngOnInit(): void {
		this.rxIntuitiveScanOptionsFacade.loadNiriInIntuitiveRx().pipe(takeUntil(this.componentAlive$)).subscribe();
		this.rxIntuitiveScanOptionsFacade.loadSleeveConfirmation().pipe(takeUntil(this.componentAlive$)).subscribe();
		this.rxIntuitiveScanOptionsFacade.loadPreTreatmentAndEmergenceProfile().pipe(takeUntil(this.componentAlive$)).subscribe();
		this.rxIntuitiveScanOptionsFacade.loadMultiBiteRestoScan().pipe(takeUntil(this.componentAlive$)).subscribe();
		this.rxIntuitiveScanOptionsFacade.loadAdditionalBites().pipe(takeUntil(this.componentAlive$)).subscribe();
		this.rxIntuitiveScanOptionsFacade.handlePalatalFeedback().pipe(takeUntil(this.componentAlive$)).subscribe();
		this.palatalFeedbackClicked$
			.pipe(debounceTime(debounceDelay), distinctUntilChanged(), takeUntil(this.componentAlive$))
			.subscribe(val => this.biLogger.userActionEvent(palatalFeedbackToggleClicked(val)));
	}

	onNiriCaptureUpdate(isNiriCaptureChecked: boolean): void {
		this.rxIntuitiveScanOptionsFacade.updateNiriCapture({ isNiriCaptureChecked });
	}

	onPalatalFeedbackUpdate(isPalatalFeedbackChecked: boolean): void {
		this.rxIntuitiveScanOptionsFacade.updatePalatalFeedback({ isPalatalFeedbackChecked });
		this.palatalFeedbackClicked$.next(isPalatalFeedbackChecked);
	}

	onSleeveConfirmationUpdate(isSleeveChecked: boolean) {
		this.rxIntuitiveScanOptionsFacade.updateSleeveConfirmation(isSleeveChecked);
	}

	onPreTreatmentUpdate(isPreTreatmentChecked: boolean) {
		this.rxIntuitiveScanOptionsFacade.updatePreTreatmentConfirmation({ isPreTreatmentChecked });
	}

	onDentureCopyScanUpdate(isDentureCopyScan: boolean): void {
		this.rxIntuitiveScanOptionsFacade.updateScanOptionsDentureCopyScan(isDentureCopyScan);
	}

	onEmergencyProfileUpdate(isEmergencyProfile: boolean): void {
		this.rxIntuitiveScanOptionsFacade.updateEmergencyProfile(isEmergencyProfile);
	}

	onMultiBiteUpdate(checked: boolean): void {
		this.rxIntuitiveScanOptionsFacade.updateScanOptionsMultiBite(checked);
	}

	onRestoMultiBiteUpdate(checked: boolean): void {
		this.rxIntuitiveScanOptionsFacade.updateScanOptionsRestoMultiBite(checked);

		this.biLogger.userActionEvent(restoMultiBiteToggleClicked(checked));
	}

	onOrthoModelIcastUpdate(checked: boolean): void {
		const typeId = checked ? TypeEnum.Ortho_Model_iCast : TypeEnum.Empty;

		this.procedureTypeService.handleProcedureTypeChanged(typeId).pipe(take(1), takeUntil(this.componentAlive$)).subscribe();
	}

	getAdditionalBiteImageUrl(biteImg: string): string {
		return `assets/bites/${biteImg}.svg`;
	}

	isBiteSelected(biteId: string): Observable<boolean> {
		return this.rxIntuitiveScanOptionsFacade.isBiteSelected(biteId);
	}

	onAdditionalBiteToggle(biteId: Bites): void {
		combineLatest([this.isDisabled$, this.isBiteSelected(biteId)])
			.pipe(take(1), takeUntil(this.componentAlive$))
			.subscribe(([isDisabled, isSelected]) => {
				if (!isDisabled) {
					this.onAdditionalBitesUpdate(!isSelected, biteId);
				}
			});
	}

	onAdditionalBitesUpdate(isChecked: boolean, biteId: Bites) {
		this.biLogger.userActionEvent(restoMultiBiteClicked(isChecked, biteId));

		this.rxIntuitiveScanOptionsFacade.updateAdditionalBites({ [biteId]: isChecked });
	}
}
